import * as THREE from 'three';
import { AircraftView } from './Aircraft.js';
import { FlightAudio } from './Audio.js';
import { BirdSystem } from './Birds.js';
import { CameraRig } from './CameraRig.js';
import { Effects } from './Effects.js';
import { createFlightState, updateFlight } from './FlightModel.js';
import { InputController } from './Input.js';
import {
  COURSE,
  awardBirdEvent,
  createMissionState,
  startMission,
  updateMission
} from './Mission.js';
import { GameUI } from './UI.js';
import { World } from './World.js';

export class Game {
  constructor({ renderer, canvas, root }) {
    this.renderer = renderer;
    this.canvas = canvas;
    this.root = root;
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(58, window.innerWidth / window.innerHeight, 0.3, 22000);
    this.clock = new THREE.Clock();
    this.elapsed = 0;
    this.started = false;
    this.paused = false;
    this.finished = false;
    this.simulationAccumulator = 0;
    this.planePosition = new THREE.Vector3();

    this.configureRenderer();
    this.world = new World(this.scene);
    this.aircraft = new AircraftView(this.scene);
    this.birds = new BirdSystem(this.scene);
    this.effects = new Effects(this.scene);
    this.audio = new FlightAudio();
    this.cameraRig = new CameraRig(this.camera);
    this.flight = createFlightState();
    this.mission = createMissionState();

    const actions = {
      onStart: () => this.start(),
      onRestart: () => this.restart(),
      onPause: () => this.togglePause(),
      onCamera: () => this.cycleCamera()
    };
    this.ui = new GameUI(root, actions);
    this.input = new InputController(root, actions);
    this.aircraft.update(this.flight, 0);
    this.bindWindow();
    this.ui.ready(renderer.userData.backend);
    this.renderer.setAnimationLoop(() => this.frame());
  }

  configureRenderer() {
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.08;
  }

  bindWindow() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.7));
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden && this.started && !this.finished) this.setPaused(true);
    });
  }

  async start() {
    if (this.started && !this.finished) return;
    await this.audio.start();
    this.flight = createFlightState();
    this.mission = startMission(createMissionState());
    this.flight.gearDown = true;
    this.started = true;
    this.finished = false;
    this.paused = false;
    this.simulationAccumulator = 0;
    this.birds.reset();
    this.aircraft.update(this.flight, 0);
    this.cameraRig.modeIndex = 0;
    this.cameraRig.snap(this.aircraft.group);
    this.ui.start();
    this.ui.update(this.flight, this.mission, this.world.querySurface(0, 650));
    this.ui.toast('Engine live', 0, 'amber');
  }

  restart() {
    this.started = false;
    this.start();
  }

  setPaused(value) {
    if (!this.started || this.finished) return;
    this.paused = value;
    this.ui.setPaused(value);
    if (!value) this.clock.getDelta();
  }

  togglePause() {
    this.setPaused(!this.paused);
  }

  cycleCamera() {
    if (!this.started) return;
    const mode = this.cameraRig.cycle();
    const labels = { chase: 'Chase camera', close: 'Wing camera', cockpit: 'Cockpit camera' };
    this.ui.toast(labels[mode], 0, 'amber');
  }

  onMissionEvent(event) {
    if (!event) return;
    if (event.type === 'takeoff') {
      this.ui.toast('Positive climb', event.points);
      this.audio.event('takeoff');
    } else if (event.type === 'ring') {
      const ring = COURSE[event.index];
      this.effects.burst(new THREE.Vector3(ring.x, ring.y, ring.z), '#ffbd62', 34, 25);
      this.ui.toast(`Gate ${event.index + 1} clear`, event.points);
      this.audio.event('ring');
    } else if (event.type === 'landing') {
      this.effects.burst(this.aircraft.group.position.clone(), '#7ff4d5', 56, 22);
      this.audio.event('landing');
      this.finished = true;
      this.ui.showResult(this.mission, true);
    } else if (event.type === 'crash') {
      this.effects.burst(this.aircraft.group.position.clone(), '#ff6b3c', 75, 34);
      this.audio.event('crash');
      this.cameraRig.bump(4.8);
      this.ui.flashDamage();
      this.finished = true;
      this.ui.showResult(this.mission, false);
    }
  }

  onBirdEvent(type, position) {
    if (!this.started || this.paused || this.finished) return;
    awardBirdEvent(this.mission, type);
    this.audio.event(type);
    if (type === 'near-miss') {
      this.effects.burst(position, '#e9f5ed', 15, 12);
      this.ui.toast('Bird near miss', 250);
      this.cameraRig.bump(0.65);
    } else {
      this.effects.burst(position, '#ff8b63', 28, 20);
      this.flight.roll += Math.random() > 0.5 ? 0.32 : -0.32;
      this.flight.verticalSpeed -= 3.5;
      this.flight.speed = Math.max(22, this.flight.speed - 11);
      this.ui.toast('Bird strike', -400, 'danger');
      this.ui.flashDamage();
      this.cameraRig.bump(3.2);
    }
  }

  updateSimulationStep(dt) {
    const surface = this.world.querySurface(this.flight.position.x, this.flight.position.z);
    this.flight.gearDown = this.mission.phase === 'approach' || this.flight.grounded;
    updateFlight(this.flight, this.input.getControls(), dt, surface);
    const event = updateMission(this.mission, this.flight, dt);
    this.onMissionEvent(event);
  }

  updateActivePresentation() {
    this.planePosition.set(this.flight.position.x, this.flight.position.y, this.flight.position.z);
    this.birds.update(this.elapsed, this.planePosition, (type, position) => this.onBirdEvent(type, position));
    this.audio.update(this.flight);
    const surface = this.world.querySurface(this.flight.position.x, this.flight.position.z);
    this.ui.update(this.flight, this.mission, surface);
  }

  updateIntroCamera() {
    const drift = Math.sin(this.elapsed * 0.17);
    const compact = window.innerWidth < 600;
    this.camera.position.set(
      this.flight.position.x - 29 + drift * 3.5,
      this.flight.position.y + 13.5 + Math.sin(this.elapsed * 0.23) * 1.2,
      this.flight.position.z - 30
    );
    this.camera.lookAt(
      this.flight.position.x + (compact ? -7 : 0),
      this.flight.position.y + (compact ? -13 : 0.8),
      this.flight.position.z - 2
    );
  }

  frame() {
    const dt = Math.min(this.clock.getDelta(), 0.5);
    this.elapsed += dt;
    if (this.started && !this.paused && !this.finished) {
      const fixedStep = 1 / 60;
      this.simulationAccumulator = Math.min(0.5, this.simulationAccumulator + dt);
      let steps = 0;
      while (this.simulationAccumulator >= fixedStep && steps < 30 && !this.finished) {
        this.updateSimulationStep(fixedStep);
        this.simulationAccumulator -= fixedStep;
        steps += 1;
      }
      this.updateActivePresentation();
    } else if (!this.started) {
      this.birds.update(this.elapsed);
    }

    this.aircraft.update(this.flight, dt);
    this.effects.update(this.paused ? 0 : dt);
    const activeRing = this.mission.phase === 'course' ? this.mission.activeRing : -1;
    this.world.update(this.elapsed, activeRing, this.mission.rings);

    if (this.started) this.cameraRig.update(this.aircraft.group, this.flight, dt);
    else this.updateIntroCamera();
    this.renderer.render(this.scene, this.camera);
  }
}
